/**
 * Column work, the way the books write it (L2.11–L2.13, L3.1 review, L4.2):
 *   add      — work the ones first; when a column makes ten or more, "write the new ten in the box above the tens."
 *   subtract — "If you don't have enough ones — go get a 10!" Tap the digit you take a ten from: it is crossed off,
 *              the smaller number is written above it, and the ten ones join the column to its right.
 * The student types each answer digit right to left. Focus moves left after each digit; Backspace moves back.
 * Only the finished answer is judged (the misconception library reads the whole number).
 */
export interface VerticalWork {
  el: HTMLElement;
  value: () => string;
  /** Did the student use a trade (a carry box or a "get a ten")? For the research data. */
  traded: () => boolean;
  focus: () => void;
  lock: () => void;
  onSubmit: (fn: () => void) => void;
}

export function verticalWork(top: number, bottom: number, op: "+" | "−" | "×"): VerticalWork {
  const res = op === "+" ? top + bottom : op === "×" ? top * bottom : top - bottom;
  const trades = op !== "−";
  const cols = Math.max(String(top).length, String(bottom).length, String(res).length);
  const pad = (n: number) => String(n).padStart(cols, " ").split("");
  const t = pad(top), b = pad(bottom);
  const el = document.createElement("div");
  el.className = "colwork";
  el.setAttribute("role", "group");
  el.setAttribute("aria-label", `${top} ${op === "+" ? "plus" : op === "×" ? "times" : "minus"} ${bottom}, worked in columns`);
  // grid: [op column] + cols columns
  const cell = (cls: string, html = "") => `<div class="cw ${cls}">${html}</div>`;
  let h = "";
  // Row 1: trade boxes (add) or the renamed digits (subtract)
  h += cell("op");
  for (let c = 0; c < cols; c++) {
    const isOnes = c === cols - 1;
    if (trades) h += isOnes ? cell("") : cell("tradebox", `<input class="tb" data-c="${c}" inputmode="numeric" maxlength="1" aria-label="Trade box above column ${cols - c}" autocomplete="off">`);
    else h += cell("renamed", `<span data-r="${c}"></span>`);
  }
  // Row 2: top
  h += cell("op");
  for (let c = 0; c < cols; c++) {
    const d = t[c]!.trim();
    const canGet = op === "−" && d !== "" && c < cols - 1;
    h += cell("dig top", canGet ? `<button type="button" class="get" data-c="${c}" aria-label="Go get a ten from the ${d}">${d}</button>` : d);
  }
  // Row 3: bottom with sign
  h += cell("op", op);
  for (let c = 0; c < cols; c++) h += cell("dig bot", b[c]!.trim());
  // Row 4: answer
  h += cell("op");
  for (let c = 0; c < cols; c++) h += cell("ansc", `<input class="ad" data-c="${c}" inputmode="numeric" maxlength="1" aria-label="Answer digit, column ${cols - c} from the right" autocomplete="off">`);
  el.innerHTML = `<div class="cwgrid" style="grid-template-columns:28px repeat(${cols},44px)">${h}</div>
    ${op === "−" ? '<p class="note" style="margin:6px 0 0">Not enough to take away? Tap the digit you go get a ten from.</p>' : op === "×" ? '<p class="note" style="margin:6px 0 0">Ones first: multiply, write the ones, trade the tens next door (the box above). Then multiply the tens and add the traded ones in.</p>' : '<p class="note" style="margin:6px 0 0">Ones first. A new ten goes in the box above the tens.</p>'}`;

  const ans = [...el.querySelectorAll<HTMLInputElement>("input.ad")];
  const tbs = [...el.querySelectorAll<HTMLInputElement>("input.tb")];
  let submitFn: () => void = () => {};
  let usedTrade = false;

  // Typing: digits only; move left after a digit (via the trade box above the next column, for addition).
  const order: HTMLInputElement[] = [];
  for (let c = cols - 1; c >= 0; c--) {
    order.push(ans[c]!);
    if (trades && c - 1 >= 0) order.push(tbs.find((x) => Number(x.dataset.c) === c - 1)!);
  }
  order.forEach((inp, i) => {
    inp.addEventListener("input", () => {
      inp.value = inp.value.replace(/\D/g, "").slice(-1);
      if (inp.classList.contains("tb") && inp.value) usedTrade = true;
      if (inp.value && order[i + 1]) order[i + 1]!.focus();
    });
    inp.addEventListener("keydown", (e) => {
      if (e.key === "Backspace" && !inp.value && order[i - 1]) { e.preventDefault(); order[i - 1]!.focus(); }
      if (e.key === "Enter") { e.preventDefault(); submitFn(); }
      if (e.key === "ArrowLeft" && order[i + 1]) { e.preventDefault(); order[i + 1]!.focus(); }
      if (e.key === "ArrowRight" && order[i - 1]) { e.preventDefault(); order[i - 1]!.focus(); }
    });
  });

  // Subtraction: go get a ten. Each tap takes one ten from that digit (crossed off, smaller number above) and gives
  // ten ones to the column on its right. Taking from a 0 is allowed only after it has received ten (across zeros).
  const cur = t.map((d) => (d.trim() === "" ? null : Number(d)));
  el.querySelectorAll<HTMLButtonElement>("button.get").forEach((btn) => {
    btn.onclick = () => {
      const c = Number(btn.dataset.c);
      const v = cur[c];
      if (v == null || v <= 0) return;
      cur[c] = v - 1;
      cur[c + 1] = (cur[c + 1] ?? 0) + 10;
      usedTrade = true;
      const show = (k: number) => {
        const r = el.querySelector<HTMLElement>(`[data-r="${k}"]`)!;
        r.textContent = String(cur[k]);
        const topCell = el.querySelectorAll<HTMLElement>(".dig.top")[k]!;
        topCell.classList.add("crossed");
      };
      show(c);
      show(c + 1);
      ans[cols - 1]?.focus();
    };
  });

  return {
    el,
    value: () => ans.map((i) => i.value).join("").replace(/^0+(?=\d)/, ""),
    traded: () => usedTrade,
    focus: () => ans[cols - 1]?.focus(),
    lock: () => { ans.forEach((i) => (i.disabled = true)); tbs.forEach((i) => (i.disabled = true)); el.querySelectorAll<HTMLButtonElement>("button.get").forEach((x) => (x.disabled = true)); },
    onSubmit: (fn) => (submitFn = fn),
  };
}
